import FileDisplay from "@/modules/vendors/third-party-evaluation-form/components/FileDisplay";
import { Button, cn, Tooltip } from "@nextui-org/react";
import { ArrowUp, PaperclipIcon } from "lucide-react";
import { forwardRef, useCallback, useRef } from "react";
import SharedPromptInput from "./SharedPromptInput";

export interface SharedChatInputProps {
  message: string;
  onMessageChange: (message: string) => void;
  onSendMessage: () => void;
  isGenerating?: boolean;
  isDisabled?: boolean;
  placeholder?: string;
  maxLength?: number;
  showFileUpload?: boolean;
  showCharacterCount?: boolean;
  helpText?: string;
  onFileUpload?: (files: File[]) => void;
  uploadedFiles?: Array<{ name?: string; id: string; status?: string; file?: File; errorMessage?: string }>;
  uploadProgress?: { [key: string]: number };
  onRemoveFile?: (fileId: string) => void;
  fileUploadError?: boolean;
  fileUploadErrorMessage?: string;
  maxFiles?: number;
  allowedFileTypes?: string[];
  fileTypesTooltipText?: string; // Custom text for file types in tooltip
  className?: string;
}

export default forwardRef<HTMLTextAreaElement, SharedChatInputProps>(function SharedChatInput({
  message,
  onMessageChange,
  onSendMessage,
  isGenerating = false,
  isDisabled = false,
  placeholder = "Type your message...",
  maxLength = 10000,
  showFileUpload = false,
  showCharacterCount = true,
  helpText,
  onFileUpload,
  uploadedFiles = [],
  uploadProgress = {},
  onRemoveFile,
  fileUploadError = false,
  fileUploadErrorMessage,
  maxFiles = 10,
  allowedFileTypes = [
    "application/json",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ],
  fileTypesTooltipText,
  className
}: SharedChatInputProps, ref: React.ForwardedRef<HTMLTextAreaElement>) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const displayContainerRef = useRef<HTMLDivElement>(null);

  const handleSendMessage = useCallback(() => {
    const hasMessage = message && message.trim();
    const hasFiles = uploadedFiles.length > 0;
    
    // Check if any files are still uploading
    const hasUploadingFiles = uploadedFiles.some(
      file => file.status === 'gettingurl' || file.status === 'inprogress'
    );
    
    if ((hasMessage || hasFiles) && !isGenerating && !fileUploadError && !isDisabled && !hasUploadingFiles) {
      onSendMessage();
    }
  }, [message, uploadedFiles, isGenerating, fileUploadError, isDisabled, onSendMessage]);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    if (files.length > 0 && onFileUpload) {
      onFileUpload(files);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [onFileUpload]);

  const handleFileButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const hasMessage = message && message.trim();
  const hasFiles = uploadedFiles.length > 0;
  const hasUploadingFiles = uploadedFiles.some(
    file => file.status === 'gettingurl' || file.status === 'inprogress'
  );
  const hasErrorFiles = uploadedFiles.some(
    file => file.status === 'error'
  );
  const canSendMessage = (hasMessage || hasFiles) && !isGenerating && !fileUploadError && !isDisabled && !hasUploadingFiles && !hasErrorFiles;

  return (
    <div className={className}>
      <form className="flex w-full flex-col items-start rounded-medium bg-default-100 transition-colors hover:bg-default-200/70">
        {/* File upload display - Using FileDisplay component with progress */}
        <div className="pt-3">
          {uploadedFiles.length > 0 && (
            <div className="flex pl-4">
              <div className="flex flex-wrap gap-2" ref={displayContainerRef}>
                {uploadedFiles.map((file) => {
                  const fileName = file.name || (file.file ? file.file.name : 'Unknown');
                  const fileProgress = file.file ? uploadProgress[file.file.name] : undefined;
                  
                  return (
                    <FileDisplay
                      key={file.id}
                      fileName={fileName}
                      status={file.status as any || 'success'}
                      showRemoveButton={!!onRemoveFile}
                      onRemove={() => onRemoveFile?.(file.id)}
                      size="sm"
                      progress={fileProgress}
                      errorMessage={file.errorMessage}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Input area */}
        {maxLength > 0 ? (
          <SharedPromptInput
            ref={ref}
            placeholder={placeholder}
            onKeyDown={(event: any) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                // Only send if not generating and all conditions are met
                if (canSendMessage) {
                  handleSendMessage();
                }
              }
            }}
            classNames={{
              inputWrapper: "!bg-transparent shadow-none",
              innerWrapper: "relative",
              input: "pt-1 pl-2 pb-6 !pr-10 text-sm",
            }}
            endContent={
              <div className="flex items-end gap-2">
                <Tooltip
                  showArrow
                  content="Send message"
                  delay={500}
                  closeDelay={0}
                  classNames={{
                    content: "bg-black/95 backdrop-blur-md text-white text-tiny p-3",
                    arrow: "backdrop-blur-md",
                    base: "before:bg-black/95",
                  }}
                >
                  <Button
                    onClick={handleSendMessage}
                    isIconOnly
                    color={!canSendMessage ? "default" : "primary"}
                    isDisabled={!canSendMessage}
                    radius="lg"
                    size="sm"
                    variant="solid"
                  >
                    <ArrowUp
                      className={cn(
                        "[&>path]:stroke-[2px]",
                        !canSendMessage ? "text-default-600" : "text-primary-foreground",
                      )}
                    />
                  </Button>
                </Tooltip>
                </div>
              }
              minRows={3}
              radius="lg"
              value={message}
              variant="flat"
              onValueChange={onMessageChange}
              isDisabled={false}
              maxLength={maxLength}
            />
          ) : null}
        
        {/* Bottom toolbar */}
        <div className="flex w-full items-center justify-between gap-2 px-4 pb-4">
          <div className={`flex w-full gap-1 md:gap-3 ${isGenerating ? "opacity-50 pointer-events-none" : ""}`}>
            {showFileUpload && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={allowedFileTypes.join(',')}
                  onChange={handleFileChange}
                  className="hidden"
                />
                <Tooltip
                  delay={500}
                  showArrow={true}
                  closeDelay={0}
                  classNames={{
                    content: "bg-black/95 backdrop-blur-md text-white text-tiny p-3",
                    arrow: "backdrop-blur-md",
                    base: "before:bg-black/95",
                  }}
                  content={
                    <div>
                      <p>Upload up to {maxFiles} files 20MB each.</p>
                      <p>Formats accepted: {fileTypesTooltipText || allowedFileTypes.map(type => 
                        type.split('/')[1] || type.split('/')[0]
                      ).join(', ')}</p>
                    </div>
                  }
                >
                  <Button
                    size="sm"
                    startContent={<PaperclipIcon size={18} />}
                    variant="flat"
                    onClick={handleFileButtonClick}
                    isDisabled={isDisabled || isGenerating}
                  >
                    Attach
                  </Button>
                </Tooltip>
              </>
            )}
          </div>
          {showCharacterCount && (
            <p className="py-1 text-tiny text-default-400">{message.length}/{maxLength}</p>
          )}
        </div>
      </form>
      
      {fileUploadError && fileUploadErrorMessage && (
        <p className="py-2 px-2 text-tiny text-red-500 text-center">
          {fileUploadErrorMessage}
        </p>
      )}
      
      {helpText && !fileUploadError && (
        <p className="py-2 px-2 text-tiny text-default-400 text-center">
          {helpText}
        </p>
      )}
    </div>
  );
});